// Captions in the viewer's language on embedded YouTube videos (ID-220).
// Our videos only carry English auto-generated captions, so other languages come from YouTube's
// auto-translation, applied through the IFrame API once the video plays (see useYoutubeCaptions).

// YouTube caption codes per Insiders locale; an unmapped locale falls back to its own code.
export const YOUTUBE_CAPTION_LANG: Record<string, string> = {
  ar: 'ar',
  de: 'de',
  es: 'es',
  fr: 'fr',
  he: 'iw',
  it: 'it',
  pt: 'pt',
  ru: 'ru',
  tr: 'tr',
  zh: 'zh-Hans',
};

// Spoken language of our videos: viewers in it keep YouTube's default (captions off).
export const VIDEO_SPOKEN_LANG = 'en';

const ALIASES: Record<string, string> = { he: 'iw' };
const sameLang = (a?: string | null, b?: string | null) => {
  const norm = (v?: string | null) => {
    const s = (v ?? '').toLowerCase();
    return ALIASES[s] ?? s;
  };
  return !!a && !!b && norm(a) === norm(b);
};

export const youtubeCaptionLang = (locale?: string | null): string | null => {
  const full = (locale ?? '').trim();
  if (!full) return null;
  const short = full.split(/[-_]/)[0]!.toLowerCase();
  if (short === VIDEO_SPOKEN_LANG) return null;
  return YOUTUBE_CAPTION_LANG[full] ?? YOUTUBE_CAPTION_LANG[short] ?? full;
};

// No `hl`: set to a language the video has no track for, YouTube loads no captions at all.
export const youtubeCaptionParams = (locale?: string | null): Record<string, string> => {
  const lang = youtubeCaptionLang(locale);
  return lang ? { cc_load_policy: '1', cc_lang_pref: lang, enablejsapi: '1' } : {};
};

export const withYoutubeCaptions = (url: string, locale?: string | null): string => {
  const params = youtubeCaptionParams(locale);
  if (!url || !Object.keys(params).length) return url;
  try {
    const u = new URL(url);
    for (const [k, v] of Object.entries(params)) u.searchParams.set(k, v);
    return u.toString();
  } catch {
    return url;
  }
};

export type YoutubeCaptionTrack = {
  languageCode?: string;
  translationLanguage?: { languageCode?: string } | null;
  [key: string]: unknown;
};

// Track option that auto-translates the loaded track; null when it already shows `lang` or none is loaded.
export const translatedCaptionTrack = (
  current: YoutubeCaptionTrack | null | undefined,
  lang: string,
): YoutubeCaptionTrack | null => {
  if (!current?.languageCode) return null;
  if (sameLang(current.languageCode, lang) || sameLang(current.translationLanguage?.languageCode, lang)) return null;
  return { ...current, translationLanguage: { languageCode: lang } };
};
